
import React, { useState } from 'react';
import { X, Download, FileText, AlertCircle, CheckCircle2, TrendingUp } from 'lucide-react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const PDFExportModal = ({ isOpen, onClose, scorecardData, selectedPeriod, globalPerf }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [exportFormat, setExportFormat] = useState('html');

  if (!isOpen) return null;

  const getHistoricalPeriods = () => {
    if (!selectedPeriod) return ["H-3", "H-2", "H-1"];

    const monthMap = {
      jan: 1, fev: 2, mar: 3, abr: 4, mai: 5, jun: 6,
      jul: 7, ago: 8, set: 9, out: 10, nov: 11, dez: 12,
    };

    const reverseMonthMap = {
      1: "jan", 2: "fev", 3: "mar", 4: "abr", 5: "mai", 6: "jun",
      7: "jul", 8: "ago", 9: "set", 10: "out", 11: "nov", 12: "dez",
    };

    let month, year;

    if (selectedPeriod.includes("-") && selectedPeriod.match(/^[a-z]{3}-\d{4}$/i)) {
      const [monthStr, yearStr] = selectedPeriod.split("-");
      month = monthMap[monthStr.toLowerCase()];
      year = parseInt(yearStr);
    } else if (selectedPeriod.includes("/")) {
      const [monthStr, yearStr] = selectedPeriod.split("/");
      month = monthMap[monthStr.toLowerCase()];
      year = parseInt(`20${yearStr}`);
    }

    if (!month || !year) return ["H-3", "H-2", "H-1"];

    const periods = [];
    for (let i = 3; i >= 1; i--) {
      let calcMonth = month - i;
      let calcYear = year;

      if (calcMonth <= 0) {
        calcMonth += 12;
        calcYear -= 1;
      }

      const monthStr = reverseMonthMap[calcMonth];
      const yearStr = selectedPeriod.includes("/")
        ? calcYear.toString().slice(-2)
        : calcYear.toString();

      const separator = selectedPeriod.includes("/") ? "/" : "-";
      periods.push(`${monthStr}${separator}${yearStr}`);
    }

    return periods;
  };

  const formatValue = (value, unit) => {
    if (typeof value === "number") {
      if (unit === "R$") {
        return `R$ ${value.toLocaleString("pt-BR", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}`;
      }

      if (unit === "%") {
        return `${value.toFixed(2)}%`;
      }

      return `${value.toFixed(2)} ${unit}`;
    }

    return `${value} ${unit}`;
  };

  const getHistoryValue = (indicator, periodLabel) => {
    const historyItem = indicator.history?.find(
      (h) => h.periodLabel === periodLabel,
    );
    return historyItem ? historyItem.percent : "N/D";
  };

  const getPerspectiveIcon = (perspective) => {
    switch (perspective.toUpperCase()) {
      case "FINANCEIRO":
        return "💰";
      case "CLIENTES":
        return "👥";
      case "PROCESSOS":
        return "⚙️";
      case "PESSOAS":
        return "👤";
      default:
        return "📊";
    }
  };

  const getPerspectiveColor = (perspective) => {
    switch (perspective.toUpperCase()) {
      case "FINANCEIRO":
        return "#d1fae5";
      case "CLIENTES":
        return "#dbeafe";
      case "PROCESSOS":
        return "#e9d5ff";
      case "PESSOAS":
        return "#fed7aa";
      default:
        return "#f3f4f6";
    }
  };

  // Cor por faixa de performance (mesma régua do dashboard)
  const perfColor = (v) => (v >= 100 ? "#059669" : v >= 80 ? "#d97706" : "#dc2626");

  // Monta o card "Performance Global" (idêntico ao topo do Fluxo Estratégico)
  const buildPerfCard = () => {
    if (!globalPerf || typeof globalPerf.performance !== "number") return "";
    const perf = globalPerf.performance;
    const boxColor = perfColor(perf);
    const period = globalPerf.periodLabel ? ` — ${globalPerf.periodLabel}` : "";

    let trendHTML = "";
    if (globalPerf.previous != null) {
      const diff = perf - globalPerf.previous;
      const trendColor =
        globalPerf.trend === "up" ? "#059669" : globalPerf.trend === "down" ? "#dc2626" : "#6b7280";
      const arrow = globalPerf.trend === "up" ? "↗" : globalPerf.trend === "down" ? "↘" : "—";
      trendHTML = `<span style="color:${trendColor}; font-size:14px; font-weight:600; margin-left:10px;">${arrow} ${diff.toFixed(1)} p.p.</span>`;
    }

    const miniCards = (globalPerf.perspectives || [])
      .map(
        (p) => `
          <div style="padding:6px 12px; border:1px solid #e5e7eb; border-radius:8px; background:#f9fafb; min-width:95px; text-align:left;">
            <div style="font-size:11px; color:#6b7280;">${p.perspective}</div>
            <div style="font-size:18px; font-weight:bold; color:${perfColor(p.performance)};">${p.performance.toFixed(1)}%</div>
          </div>`,
      )
      .join("");

    return `
  <div style="display:flex; align-items:center; justify-content:space-between; gap:16px;
              border:1px solid #e5e7eb; border-radius:14px; padding:16px 20px; margin-bottom:22px;
              background:#ffffff; box-shadow:0 2px 8px rgba(0,0,0,0.06);">
    <div style="display:flex; align-items:center; gap:16px;">
      <div style="width:56px; height:56px; border-radius:14px; background:${boxColor};
                  display:flex; align-items:center; justify-content:center;">
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#ffffff"
             stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="m12 14 4-4"/><path d="M3.34 19a10 10 0 1 1 17.32 0"/>
        </svg>
      </div>
      <div>
        <div style="font-size:14px; color:#475569; font-weight:600;">Performance Global${period}</div>
        <div style="display:flex; align-items:baseline;">
          <span style="font-size:40px; font-weight:bold; color:${boxColor}; line-height:1;">${perf.toFixed(1)}%</span>
          ${trendHTML}
        </div>
      </div>
    </div>
    <div style="display:flex; flex-wrap:wrap; gap:8px; justify-content:flex-end;">
      ${miniCards}
    </div>
  </div>`;
  };

  const generateHTML = () => {
    const historicalPeriods = getHistoricalPeriods();
    const currentDate = new Date().toLocaleDateString("pt-BR");
    const perfCard = buildPerfCard();

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Balanced Scorecard - ${selectedPeriod.toUpperCase()}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: Arial, sans-serif;
      font-size: 11px;
      color: #000;
      padding: 20px;
      background-color: #fff;
    }
    
    .header {
      text-align: center;
      margin-bottom: 25px;
      padding-bottom: 15px;
      border-bottom: 3px solid #1e40af;
    }
    
    .header h1 {
      color: #1e40af;
      font-size: 28px;
      margin-bottom: 8px;
      font-weight: bold;
      text-transform: uppercase;
    }
    
    .header p {
      color: #666;
      font-size: 14px;
      margin: 2px 0;
    }
    
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 25px;
      page-break-inside: auto;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    }
    
    tr {
      page-break-inside: avoid;
      page-break-after: auto;
    }
    
    thead tr {
      background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
      color: white;
    }
    
    th {
      padding: 12px 8px;
      text-align: center;
      font-weight: bold;
      border: 1px solid #ddd;
      font-size: 10px;
      text-transform: uppercase;
      /* 'top' evita bug do html2canvas em células com rowspan (texto sumia) */
      vertical-align: top;
    }

    td {
      padding: 10px 8px;
      border: 1px solid #ddd;
      text-align: left;
      /* 'top' evita bug do html2canvas em células com rowspan (texto sumia) */
      vertical-align: top;
    }
    
    .perspective-header {
      background: linear-gradient(135deg, #065f46 0%, #047857 100%);
      font-weight: bold;
      color: white;
      font-size: 13px;
      padding: 10px;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    
    .perspective-icon {
      display: inline-block;
      margin-right: 10px;
      font-size: 16px;
    }
    
    .objective-cell {
      background-color: #f8fafc;
      font-weight: 600;
      color: #1e293b;
      border-left: 4px solid #3b82f6;
    }
    
    .status-badge {
      display: inline-block;
      padding: 6px 12px;
      border-radius: 6px;
      font-weight: bold;
      font-size: 11px;
      text-align: center;
      color: white;
    }
    
    .status-green {
      background: linear-gradient(135deg, #059669 0%, #10b981 100%);
    }
    
    .status-yellow {
      background: linear-gradient(135deg, #d97706 0%, #f59e0b 100%);
    }
    
    .status-red {
      background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
    }
    
    .history-col {
      background-color: #e5e7eb;
      text-align: center;
      color: #6b7280;
      font-weight: 500;
    }
    
    .current-col {
      background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
      text-align: center;
      font-weight: bold;
      color: #065f46;
    }
    
    .performance-cell {
      font-size: 12px;
      font-weight: 900;
    }
    
    .trend-icon {
      font-size: 16px;
      vertical-align: middle;
    }
    
    .trend-up-green {
      color: #059669;
    }
    
    .trend-up-red {
      color: #dc2626;
    }
    
    .trend-down-red {
      color: #dc2626;
    }
    
    .trend-down-green {
      color: #059669;
    }
    
    .trend-neutral {
      color: #3b82f6;
    }
    
    .trend-na {
      color: #9ca3af;
    }
    
    .status-icon {
      font-size: 16px;
      vertical-align: middle;
      font-weight: bold;
    }
    
    .status-achieved {
      color: #059669;
    }
    
    .status-not-achieved {
      color: #d97706;
    }
    
    .na-text {
      color: #9ca3af;
      text-align: center;
      font-style: italic;
    }
    
    .footer {
      margin-top: 30px;
      padding: 20px;
      background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
      border-radius: 8px;
      border: 1px solid #cbd5e1;
    }
    
    .footer h3 {
      color: #1e40af;
      margin-bottom: 15px;
      font-size: 16px;
      border-bottom: 2px solid #1e40af;
      padding-bottom: 5px;
    }
    
    .legend-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 15px;
      margin-top: 10px;
    }
    
    .legend-item {
      background: white;
      padding: 10px;
      border-radius: 6px;
      border-left: 4px solid #3b82f6;
    }
    
    .legend-item strong {
      color: #1e40af;
    }
    
    @media print {
      body {
        padding: 15px;
      }
      
      .no-print {
        display: none;
      }
      
      table {
        page-break-inside: auto;
      }
      
      tr {
        page-break-inside: avoid;
        page-break-after: auto;
      }
      
      .footer {
        page-break-inside: avoid;
      }
    }
    
    @page {
      margin: 0.5in;
      size: A4 landscape;
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>Balanced Scorecard</h1>
    <p><strong>Período de Referência:</strong> ${selectedPeriod.toUpperCase()}</p>
    <p><strong>Gerado em:</strong> ${currentDate}</p>
  </div>

  ${perfCard}

  <table>
    <thead>
      <tr>
        <th style="width: 22%;" rowspan="2">OBJETIVO ESTRATÉGICO</th>
        <th style="width: 8%;" rowspan="2">STATUS</th>
        <th style="width: 24%;" rowspan="2">ITEM DE CONTROLE</th>
        <th colspan="3" style="background-color: #6b7280;">HISTÓRICO</th>
        <th colspan="3" style="background-color: #10b981;">${selectedPeriod.toUpperCase()}</th>
        <th style="width: 8%;" rowspan="2">STATUS T</th>
        <th style="width: 6%;" rowspan="2">TREND</th>
      </tr>
      <tr>
        <th class="history-col" style="width: 6%;">${historicalPeriods[0]?.toUpperCase() || "H-3"}</th>
        <th class="history-col" style="width: 6%;">${historicalPeriods[1]?.toUpperCase() || "H-2"}</th>
        <th class="history-col" style="width: 6%;">${historicalPeriods[2]?.toUpperCase() || "H-1"}</th>
        <th class="current-col" style="width: 8%;">REALIZADO</th>
        <th class="current-col" style="width: 8%;">META</th>
        <th class="current-col" style="width: 6%;">%</th>
      </tr>
    </thead>
    <tbody>
      ${(scorecardData || []).map(perspective => `
        <tr>
          <td colspan="11" class="perspective-header">
            <span class="perspective-icon">${getPerspectiveIcon(perspective.perspective)}</span>
            ${perspective.perspective}
          </td>
        </tr>
        ${perspective.objectives?.map(objective =>
      objective.indicators?.map((indicator, indIndex) => {
        const isFirstIndicator = indIndex === 0;
        const rowspan = objective.indicators.length;
        const average = objective.average || 0;
        const statusClass = average >= 100 ? 'status-green' : average >= 80 ? 'status-yellow' : 'status-red';

        return `
              <tr>
                ${isFirstIndicator ? `
                  <td rowspan="${rowspan}" class="objective-cell">${objective.objective}</td>
                  <td rowspan="${rowspan}" style="text-align: center;">
                    <span class="status-badge ${statusClass}">${average.toFixed(1)}%</span>
                  </td>
                ` : ''}
                <td>${indicator.indicator}</td>
                <td class="na-text">
                  ${getHistoryValue(indicator, historicalPeriods[0]) !== "N/D"
            ? formatValue(getHistoryValue(indicator, historicalPeriods[0]), indicator.unit)
            : "N/D"}
                </td>
                <td class="na-text">
                  ${getHistoryValue(indicator, historicalPeriods[1]) !== "N/D"
            ? formatValue(getHistoryValue(indicator, historicalPeriods[1]), indicator.unit)
            : "N/D"}
                </td>
                <td class="na-text">
                  ${getHistoryValue(indicator, historicalPeriods[2]) !== "N/D"
            ? formatValue(getHistoryValue(indicator, historicalPeriods[2]), indicator.unit)
            : "N/D"}
                </td>
                <td class="current-col">
                  ${indicator.current ? formatValue(indicator.current.realized, indicator.unit) : "N/D"}
                </td>
                <td class="current-col">
                  ${indicator.current ? formatValue(indicator.current.target, indicator.unit) : "N/D"}
                </td>
                <td class="current-col performance-cell">
                  ${indicator.current ? `${indicator.current.percent.toFixed(1)}%` : "N/D"}
                </td>
                <td style="text-align: center;">
                  <span class="status-icon ${indicator.current?.statusT === 'T' ? 'status-achieved' : 'status-not-achieved'}">
                    ${indicator.current?.statusT === 'T' ? '✓' : indicator.current?.statusT === 'N' ? '!' : 'N/D'}
                  </span>
                </td>
                <td style="text-align: center;">
                  <span class="trend-icon ${indicator.current?.trend === 'upV' ? 'trend-up-green' :
            indicator.current?.trend === 'upR' ? 'trend-up-red' :
              indicator.current?.trend === 'downR' ? 'trend-down-red' :
                indicator.current?.trend === 'downV' ? 'trend-down-green' :
                  indicator.current?.trend === 'w' ? 'trend-neutral' : 'trend-na'
          }">
                    ${indicator.current?.trend === 'upV' ? '↗' :
            indicator.current?.trend === 'upR' ? '↗' :
              indicator.current?.trend === 'downR' ? '↘' :
                indicator.current?.trend === 'downV' ? '↘' :
                  indicator.current?.trend === 'w' ? '—' :
                    'N/D'
          }
                  </span>
                </td>
              </tr>
            `;
      }).join('')
    ).join('')}
      `).join('')}
    </tbody>
  </table>

  <div class="footer">
    <h3>📋 Legenda e Instruções</h3>
    <div class="legend-grid">
      <div class="legend-item">
        <p><strong>STATUS T:</strong> ✓ = Meta atingida | ! = Meta não atingida</p>
      </div>
      <div class="legend-item">
        <p><strong>TREND:</strong> ↗ = Alta (Verde=Favorável/Vermelho=Desfavorável) | ↘ = Baixa (Verde=Favorável/Vermelho=Desfavorável) | — = Neutro</p>
      </div>
      <div class="legend-item">
        <p><strong>STATUS %:</strong> <span style="color: #059669;">Verde ≥ 100%</span> | <span style="color: #d97706;">Amarelo ≥ 80%</span> | <span style="color: #dc2626;">Vermelho < 80%</span></p>
      </div>
      <div class="legend-item">
        <p><strong>N/D:</strong> Não disponível (sem dados históricos)</p>
      </div>
    </div>
    <p style="margin-top: 15px; text-align: center; color: #64748b; font-style: italic;">
      Relatório gerado automaticamente pelo BSC — Marply
    </p>
  </div>
</body>
</html>
    `;
  };

  // Renderiza o HTML do relatório num iframe oculto (fidelidade total ao layout)
  // e retorna o <body> já renderizado para o html2canvas capturar.
  const renderReportInIframe = () => {
    return new Promise((resolve, reject) => {
      const iframe = document.createElement('iframe');
      // Largura fixa em px p/ layout paisagem consistente (independe da tela).
      iframe.style.position = 'fixed';
      iframe.style.left = '-10000px';
      iframe.style.top = '0';
      iframe.style.width = '1123px'; // ~A4 paisagem @96dpi
      iframe.style.height = '100px';
      iframe.style.border = '0';
      iframe.setAttribute('aria-hidden', 'true');
      document.body.appendChild(iframe);

      const cleanup = () => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      };

      iframe.onload = () => {
        try {
          const doc = iframe.contentDocument;
          const body = doc.body;
          // Ajusta a altura do iframe ao conteúdo real
          const fullHeight = Math.max(body.scrollHeight, doc.documentElement.scrollHeight);
          iframe.style.height = `${fullHeight}px`;
          // Pequena espera p/ garantir fontes/emoji/gradientes pintados
          setTimeout(() => resolve({ iframe, body, cleanup }), 350);
        } catch (e) {
          cleanup();
          reject(e);
        }
      };

      const doc = iframe.contentDocument;
      doc.open();
      doc.write(generateHTML());
      doc.close();
    });
  };

  const handleExport = async () => {
    setIsGenerating(true);

    let cleanupFn = null;
    try {
      const { body, cleanup } = await renderReportInIframe();
      cleanupFn = cleanup;

      const canvas = await html2canvas(body, {
        scale: 2,               // nitidez (retina)
        useCORS: true,
        backgroundColor: '#ffffff',
        windowWidth: 1123,
        scrollX: 0,
        scrollY: 0,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4'); // paisagem, mm, A4
      const pageW = pdf.internal.pageSize.getWidth();   // 297
      const pageH = pdf.internal.pageSize.getHeight();  // 210
      const margin = 6;
      const imgW = pageW - margin * 2;
      const imgH = (canvas.height * imgW) / canvas.width;

      // Fatiamento multipágina
      let heightLeft = imgH;
      let position = margin;
      pdf.addImage(imgData, 'PNG', margin, position, imgW, imgH, undefined, 'FAST');
      heightLeft -= (pageH - margin * 2);

      while (heightLeft > 0) {
        position = margin - (imgH - heightLeft);
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', margin, position, imgW, imgH, undefined, 'FAST');
        heightLeft -= (pageH - margin * 2);
      }

      const timestamp = new Date().toISOString().split("T")[0];
      pdf.save(`balanced-scorecard-${selectedPeriod}-${timestamp}.pdf`);

      cleanupFn && cleanupFn();
      setIsGenerating(false);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      cleanupFn && cleanupFn();
      setIsGenerating(false);
      alert('❌ Erro ao gerar o PDF. Verifique o console para mais detalhes.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Exportar para PDF</h2>
              <p className="text-gray-600">Gerar relatório do Balanced Scorecard</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Preview */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Preview do Relatório
            </h3>

            <div className="border rounded-lg overflow-hidden bg-gray-50 p-4">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-center mb-4">
                  <h1 className="text-2xl font-bold text-blue-900 mb-2">BALANCED SCORECARD</h1>
                  <p className="text-gray-600">Período: {selectedPeriod.toUpperCase()}</p>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-blue-700 text-white">
                        <th className="p-2 border text-left">OBJETIVO ESTRATÉGICO</th>
                        <th className="p-2 border">STATUS</th>
                        <th className="p-2 border text-left">ITEM DE CONTROLE</th>
                        <th className="p-2 border bg-gray-600">HISTÓRICO</th>
                        <th className="p-2 border bg-green-600">REALIZADO</th>
                        <th className="p-2 border bg-green-600">META</th>
                        <th className="p-2 border bg-green-600">%</th>
                        <th className="p-2 border">STATUS T</th>
                        <th className="p-2 border">TREND</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(scorecardData || []).slice(0, 1).map((perspective) => (
                        <React.Fragment key={perspective.perspective}>
                          <tr>
                            <td colSpan="9" className="bg-green-100 p-2 font-bold text-green-900 border">
                              {getPerspectiveIcon(perspective.perspective)} {perspective.perspective}
                            </td>
                          </tr>
                          {perspective.objectives?.slice(0, 1).map((objective) =>
                            objective.indicators?.slice(0, 1).map((indicator) => (
                              <tr key={`${objective.objectiveId}-${indicator.indicatorId}`} className="border-b">
                                <td className="p-2 bg-gray-50 border text-xs">{objective.objective}</td>
                                <td className="p-2 text-center border">
                                  <span className={`px-2 py-1 rounded text-xs font-bold ${(objective.average || 0) >= 100 ? 'bg-green-100 text-green-800' :
                                    (objective.average || 0) >= 80 ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-red-100 text-red-800'
                                    }`}>
                                    {(objective.average || 0).toFixed(1)}%
                                  </span>
                                </td>
                                <td className="p-2 border text-xs">{indicator.indicator}</td>
                                <td className="p-2 text-center text-gray-400 border">N/D</td>
                                <td className="p-2 text-center bg-green-50 font-bold border">
                                  {indicator.current ? formatValue(indicator.current.realized, indicator.unit) : "N/D"}
                                </td>
                                <td className="p-2 text-center bg-green-50 font-bold border">
                                  {indicator.current ? formatValue(indicator.current.target, indicator.unit) : "N/D"}
                                </td>
                                <td className="p-2 text-center bg-green-50 font-bold border">
                                  {indicator.current ? `${indicator.current.percent.toFixed(1)}%` : "N/D"}
                                </td>
                                <td className="p-2 text-center border">
                                  {indicator.current?.statusT === 'T' ? '✓' : indicator.current?.statusT === 'N' ? '!' : 'N/D'}
                                </td>
                                <td className="p-2 text-center border">
                                  <span className={
                                    indicator.current?.trend === 'upV' ? 'text-green-600' :
                                      indicator.current?.trend === 'upR' ? 'text-red-600' :
                                        indicator.current?.trend === 'downR' ? 'text-red-600' :
                                          indicator.current?.trend === 'downV' ? 'text-green-600' :
                                            indicator.current?.trend === 'w' ? 'text-blue-600' : 'text-gray-400'
                                  }>
                                    {
                                      indicator.current?.trend === 'upV' ? '↗' :
                                        indicator.current?.trend === 'upR' ? '↗' :
                                          indicator.current?.trend === 'downR' ? '↘' :
                                            indicator.current?.trend === 'downV' ? '↘' :
                                              indicator.current?.trend === 'w' ? '—' :
                                                'N/D'
                                    }
                                  </span>
                                </td>
                              </tr>
                            ))
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="text-xs text-gray-500 text-center mt-3">
                  Preview mostra apenas a primeira perspectiva e indicador
                </p>
              </div>
            </div>
          </div>

          {/* Export Options */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Opções de Exportação</h3>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-blue-900">Como funciona:</h4>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800 mt-2">
                    <li>Clique em "Gerar PDF" abaixo</li>
                    <li>O relatório é renderizado fiel ao layout (cores, ícones e quebras)</li>
                    <li>Um arquivo <strong>.pdf</strong> em A4 paisagem é baixado automaticamente</li>
                    <li>Pronto — sem etapas manuais de impressão</li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <div className="font-semibold text-sm">Layout Profissional</div>
                  <div className="text-xs text-gray-600">Design otimizado para impressão</div>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <TrendingUp className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <div className="font-semibold text-sm">Dados Dinâmicos</div>
                  <div className="text-xs text-gray-600">Usa os dados atuais do sistema</div>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <FileText className="w-5 h-5 text-purple-600 mt-0.5" />
                <div>
                  <div className="font-semibold text-sm">Formato Padronizado</div>
                  <div className="text-xs text-gray-600">Segue layout corporativo</div>
                </div>
              </div>
            </div>
          </div>

          {/* Export Button */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
            >
              Cancelar
            </button>
            <button
              onClick={handleExport}
              disabled={isGenerating || !scorecardData || scorecardData.length === 0}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg flex items-center justify-center gap-3 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Gerando PDF...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  Gerar PDF
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFExportModal;
